import { useState } from 'react'
import { CheckCircle } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Grid2 as Grid,
  Paper,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCustomerApplications } from '@/api/financial/customer/list-customer-applications'
import { AppHeader } from '@/components/app-header'
import { RouterLink } from '@/components/router-link'
import { routes } from '@/routes'
import { ApplicationCard } from './application-card'
import { CreditIdentity } from './profile/credit-identity'
import { CrmDialog } from './profile/crm-dialog'
import { ProductsForYou } from './products/products-for-you'
import { profileOf } from './products/credit-profile'
import { useReadUserProfile } from '@/api/user-manager'

const emptyMessage: Record<string, string> = {
  student:
    'Escolha abaixo um dos produtos para estudantes de Medicina e comece uma solicitação.',
  doctor:
    'Escolha abaixo um dos produtos para médicos e comece uma solicitação.',
  professional:
    'Veja abaixo o crédito disponível para o seu perfil e comece uma solicitação.',
}

export function CreditHomePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const started = Boolean(
    (location.state as { started?: boolean } | null)?.started,
  )

  const applications = useCustomerApplications()
  const { data: user } = useReadUserProfile()

  const [crmOpen, setCrmOpen] = useState(false)

  const inProgress = applications.data?.in_progress ?? []
  const profile = profileOf(user?.profile?.occupation)
  const needsCrm = profile === 'doctor' && !user?.profile?.crm
  const userHasVerifiedPhone = !!user?.alias?.find(
    (alias) => alias.type === 'phone' && alias.verified,
  )

  return (
    <>
      <AppHeader />

      <CreditIdentity />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {user && userHasVerifiedPhone === false && (
          <Grid size={12}>
            <Alert
              icon={false}
              severity="warning"
              action={
                <Button
                  size="small"
                  variant="text"
                  color="warning"
                  component={RouterLink}
                  to={routes.account}
                >
                  Verificar telefone
                </Button>
              }
            >
              Verifique seu telefone para continuar com a sua solicitação de
              crédito
            </Alert>
          </Grid>
        )}

        {needsCrm && (
          <Grid size={12}>
            <Alert
              icon={false}
              severity="warning"
              action={
                <Button
                  size="small"
                  variant="text"
                  color="warning"
                  onClick={() => setCrmOpen(true)}
                >
                  Cadastrar CRM
                </Button>
              }
            >
              Cadastre seu CRM para agilizar a análise dos produtos para
              médicos.
            </Alert>
          </Grid>
        )}

        {started && (
          <Grid size={12}>
            <Box
              role="status"
              sx={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr',
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'success.light',
                color: 'success.contrastText',
              }}
            >
              <CheckCircle />
              <Typography variant="body2">
                Solicitação iniciada. Você pode continuar agora ou depois, de
                onde parou.
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid size={12}>
          {inProgress.length > 0 ? (
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Solicitação em andamento</Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate(routes.credit.applications)}
                >
                  Ver solicitações
                </Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {inProgress.map((application) => (
                  <ApplicationCard
                    key={application.application_key}
                    application={application}
                  />
                ))}
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">
                Você ainda não tem solicitações
              </Typography>
              <Typography color="text.secondary" mt={0.5}>
                {applications.isError
                  ? 'Não foi possível carregar as suas solicitações agora.'
                  : emptyMessage[profile]}
              </Typography>
            </Paper>
          )}
        </Grid>

        <Grid size={12}>
          <ProductsForYou
            hasApplicationInProgress={inProgress.length > 0}
            currentProduct={inProgress[0]?.product}
          />
        </Grid>
      </Grid>

      {needsCrm && (
        <CrmDialog
          open={crmOpen}
          mode="register"
          onClose={() => setCrmOpen(false)}
        />
      )}
    </>
  )
}
